import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, ArrowLeft, Plus, Trash2, FileText, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ManageResources() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'notes',
    access_type: 'free',
    price: '0',
    file_url: ''
  });

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const response = await axios.get(`${API}/resources/my`, getAuthHeader());
      setResources(response.data);
    } catch (error) {
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/resources`,
        { ...formData, price: parseFloat(formData.price) },
        getAuthHeader()
      );
      toast.success('Resource created successfully!');
      setShowDialog(false);
      setFormData({ title: '', description: '', type: 'notes', access_type: 'free', price: '0', file_url: '' });
      fetchResources();
    } catch (error) {
      toast.error('Failed to create resource');
    }
  };

  const handleDelete = async (resourceId) => {
    if (!confirm('Are you sure you want to delete this resource?')) return;
    
    try {
      await axios.delete(`${API}/resources/${resourceId}`, getAuthHeader());
      toast.success('Resource deleted');
      fetchResources();
    } catch (error) {
      toast.error('Failed to delete resource');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate('/teacher/dashboard')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-2">Manage Resources</h1>
            <p className="text-lg text-slate-600">Upload notes, PYPs, and study materials</p>
          </div>
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button data-testid="create-resource-btn" className="rounded-full bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Resource
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl bg-white">
              <DialogHeader>
                <DialogTitle className="text-slate-900">Add New Resource</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <Label htmlFor="res-title" className="text-slate-700 font-medium">Title *</Label>
                  <Input
                    id="res-title"
                    data-testid="resource-title-input"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Mathematics Chapter 5 Notes"
                    className="text-slate-900 mt-1 bg-slate-50 border-slate-200 focus:bg-white"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="res-description" className="text-slate-700 font-medium">Description *</Label>
                  <Textarea
                    id="res-description"
                    data-testid="resource-description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="What's included in this resource?"
                    className="text-slate-900 mt-1 bg-slate-50 border-slate-200 focus:bg-white min-h-[100px]"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-700 font-medium">Type *</Label>
                    <Select
                      value={formData.type}
                      onValueChange={(value) => setFormData({ ...formData, type: value })}
                    >
                      <SelectTrigger data-testid="resource-type-select" className="text-slate-900 mt-1 bg-slate-50 border-slate-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="notes" className="text-slate-900">Notes / PDF</SelectItem>
                        <SelectItem value="pyp" className="text-slate-900">Previous Year Paper</SelectItem>
                        <SelectItem value="assignment" className="text-slate-900">Assignment</SelectItem>
                        <SelectItem value="other" className="text-slate-900">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-700 font-medium">Access Type *</Label>
                    <Select
                      value={formData.access_type}
                      onValueChange={(value) => setFormData({ ...formData, access_type: value })}
                    >
                      <SelectTrigger data-testid="access-type-select" className="text-slate-900 mt-1 bg-slate-50 border-slate-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="free" className="text-slate-900">Free</SelectItem>
                        <SelectItem value="paid" className="text-slate-900">Paid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {formData.access_type === 'paid' && (
                  <div>
                    <Label htmlFor="res-price" className="text-slate-700 font-medium">Price (INR) *</Label>
                    <Input
                      id="res-price"
                      data-testid="resource-price-input"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      placeholder="999.00"
                      className="text-slate-900 mt-1 bg-slate-50 border-slate-200 focus:bg-white"
                      required
                    />
                  </div>
                )}
                <div>
                  <Label htmlFor="res-file-url" className="text-slate-700 font-medium">File URL *</Label>
                  <Input
                    id="res-file-url"
                    data-testid="resource-file-url-input"
                    type="url"
                    value={formData.file_url}
                    onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                    placeholder="https://example.com/file.pdf"
                    className="text-slate-900 mt-1 bg-slate-50 border-slate-200 focus:bg-white"
                    required
                  />
                  <p className="text-sm text-slate-500 mt-1">Enter the URL where the file is hosted</p>
                </div>
                <Button type="submit" data-testid="submit-resource-btn" className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold h-11">
                  Create Resource
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {resources.length === 0 ? (
            <p className="text-slate-500 col-span-full text-center py-12">No resources created yet</p>
          ) : (
            resources.map((resource) => (
              <Card
                key={resource.id}
                data-testid={`resource-card-${resource.id}`}
                className="bg-white rounded-xl border border-slate-100 shadow-sm p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div className="flex items-center space-x-2">
                    {resource.access_type === 'free' ? (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-teal-100 text-teal-700 flex items-center">
                        <Unlock className="w-3 h-3 mr-1" />
                        Free
                      </span>
                    ) : (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 flex items-center">
                        <Lock className="w-3 h-3 mr-1" />
                        ₹{resource.price}
                      </span>
                    )}
                    <Button
                      data-testid={`delete-resource-${resource.id}`}
                      onClick={() => handleDelete(resource.id)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{resource.title}</h3>
                <p className="text-sm text-slate-600 mb-3 line-clamp-2">{resource.description}</p>
                <span className="text-xs text-slate-500 uppercase font-medium">{resource.type}</span>
              </Card>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
